import { useCallback, useEffect, useRef, useState } from "react";
import { BoardProductCard } from "../components/BoardProductCard";
import { Composer, type ComposerHandle } from "../components/Composer";
import { EmptyState } from "../components/EmptyState";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { assignFindToBoard, copyBoard, deleteBoard, discoverProduct, extractUrls, getBoard, getFind, removeFindFromBoard, updateBoard, updateBoardVisibility } from "../lib/api";
import { navigate } from "../lib/router";
import type { BoardDetails, BoardProduct } from "../lib/types";

function OwnerAvatar({ username, isOwner }: { username: string; isOwner: boolean }) {
  const initial = username[0]!.toUpperCase();
  const content = (
    <>
      <span className="w-6 h-6 rounded-full bg-foreground text-background flex items-center justify-center font-semibold text-xs shrink-0">
        {initial}
      </span>
      <span>@{username}</span>
    </>
  );
  if (isOwner) {
    return (
      <button
        type="button"
        onClick={() => navigate("/profile")}
        className="flex items-center gap-2 text-sm text-foreground/60 hover:text-foreground transition-colors cursor-pointer"
      >
        {content}
      </button>
    );
  }
  return (
    <span className="flex items-center gap-2 text-sm text-foreground/60">
      {content}
    </span>
  );
}

interface BoardRouteProps {
  boardId: number;
}

export function BoardRoute({ boardId }: BoardRouteProps) {
  const [details, setDetails] = useState<BoardDetails | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [pendingFindIds, setPendingFindIds] = useState<number[]>([]);
  const composerRef = useRef<ComposerHandle>(null);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelValue, setLabelValue] = useState("");
  const [copyingBoard, setCopyingBoard] = useState(false);

  const load = useCallback(async () => {
    const data = await getBoard(boardId);
    if (!data) {
      setNotFound(true);
      return null;
    }
    setDetails(data);
    return data;
  }, [boardId]);

  useEffect(() => {
    setDetails(null);
    setNotFound(false);
    load();
  }, [load]);

  useEffect(() => {
    const flashKey = `board-copy-feedback:${boardId}`;
    const message = window.sessionStorage.getItem(flashKey);
    if (!message) return;

    setFeedback(message);
    window.sessionStorage.removeItem(flashKey);
  }, [boardId]);

  useEffect(() => {
    if (notFound) {
      navigate("/", true);
    }
  }, [notFound]);

  useEffect(() => {
    if (pendingFindIds.length === 0) return;

    const timer = setTimeout(async () => {
      const updates = await Promise.all(
        pendingFindIds.map((id) => getFind(id)),
      );

      const stillPending: number[] = [];
      let needsReload = false;

      for (let i = 0; i < updates.length; i++) {
        const result = updates[i];
        if (!result) continue;
        if (result.find.status === "scraping") {
          stillPending.push(pendingFindIds[i]!);
        } else {
          needsReload = true;
        }
      }

      setPendingFindIds(stillPending);
      if (needsReload) {
        load();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [pendingFindIds, load]);

  const handleRemove = useCallback(
    async (findId: number) => {
      const ok = await removeFindFromBoard({ boardId, findId });
      if (ok) {
        setDetails((prev) =>
          prev ? { ...prev, finds: prev.finds.filter((p) => p.id !== findId) } : prev,
        );
      }
    },
    [boardId],
  );

  const handleSubmit = useCallback(
    async (value: string) => {
      const urls = extractUrls(value);
      const url = urls[0] ?? value.trim();
      if (!url) return;

      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        setFeedback("Please enter a valid URL");
        return;
      }

      composerRef.current?.setSending(true);
      setFeedback(null);

      const discoverResult = await discoverProduct({ url });

      if (!discoverResult.ok) {
        if (discoverResult.duplicate && discoverResult.find) {
          const assignResult = await assignFindToBoard({
            findId: discoverResult.find.id,
            boardId,
          });

          if (!assignResult.ok) {
            setFeedback("Failed to add item to board.");
            composerRef.current?.setSending(false);
            return;
          }

          const existingFind: BoardProduct = {
            id: discoverResult.find.id,
            url: discoverResult.find.url,
            title: discoverResult.find.title,
            imageUrl: discoverResult.find.imageUrl,
            imageScale: discoverResult.find.imageScale,
            imageOffsetX: discoverResult.find.imageOffsetX,
            imageOffsetY: discoverResult.find.imageOffsetY,
            reaction: "none",
          };

          setDetails((prev) => {
            if (!prev || prev.finds.some((candidate) => candidate.id === existingFind.id)) {
              return prev;
            }

            return { ...prev, finds: [...prev.finds, existingFind] };
          });

          composerRef.current?.setValue("");
          composerRef.current?.setSending(false);
          return;
        }

        setFeedback(discoverResult.error || "Failed to discover item.");
        composerRef.current?.setSending(false);
        return;
      }

      const find = discoverResult.data.find;

      const assignResult = await assignFindToBoard({ findId: find.id, boardId });

      if (!assignResult.ok) {
        setFeedback("Failed to add item to board.");
        composerRef.current?.setSending(false);
        return;
      }

      const newFind: BoardProduct = {
        id: find.id,
        url: find.url,
        title: find.title,
        imageUrl: find.imageUrl,
        imageScale: find.imageScale,
        imageOffsetX: find.imageOffsetX,
        imageOffsetY: find.imageOffsetY,
        reaction: "none",
      };

      setDetails((prev) => (prev ? { ...prev, finds: [...prev.finds, newFind] } : prev));

      if (find.status === "scraping") {
        setPendingFindIds((prev) => [...prev, find.id]);
      }

      composerRef.current?.setValue("");
      composerRef.current?.setSending(false);
    },
    [boardId],
  );

  const handleLabelClick = useCallback(() => {
    if (!details?.board) return;
    setLabelValue(details.board.label);
    setEditingLabel(true);
  }, [details]);

  const handleLabelSave = useCallback(async () => {
    setEditingLabel(false);
    const trimmed = labelValue.trim();
    if (!trimmed || trimmed === details?.board?.label) return;
    const result = await updateBoard(boardId, trimmed);
    if (result.ok) {
      setDetails((prev) => prev ? { ...prev, board: result.data.board } : prev);
    }
  }, [labelValue, details, boardId]);

  const handleLabelKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.currentTarget.blur();
      } else if (e.key === "Escape") {
        setEditingLabel(false);
      }
    },
    [],
  );

  const handleVisibilityToggle = useCallback(
    async (isPublic: boolean) => {
      const visibility = isPublic ? "public" : "private";
      const result = await updateBoardVisibility(boardId, visibility);
      if (result.ok) {
        setDetails((prev) => prev ? { ...prev, board: result.data.board } : prev);
      }
    },
    [boardId],
  );

  const handleDelete = useCallback(async () => {
    if (!window.confirm("Delete this board? This cannot be undone.")) return;
    const ok = await deleteBoard(boardId);
    if (ok) {
      navigate("/boards");
    }
  }, [boardId]);

  const handleCopyBoard = useCallback(async () => {
    if (copyingBoard) return;

    setCopyingBoard(true);
    setFeedback(null);

    const result = await copyBoard(boardId);

    setCopyingBoard(false);

    if (!result.ok) {
      setFeedback(result.error === "Unauthorized" ? "Sign in to copy this board." : result.error);
      return;
    }

    const summary = result.data.skippedFindCount > 0
      ? `Copied ${result.data.copiedFindCount} item${result.data.copiedFindCount === 1 ? "" : "s"}. ${result.data.skippedFindCount} already in your collection ${result.data.skippedFindCount === 1 ? "was" : "were"} left on ${result.data.skippedFindCount === 1 ? "its current board" : "their current boards"}.`
      : `Copied ${result.data.copiedFindCount} item${result.data.copiedFindCount === 1 ? "" : "s"} into your new board.`;
    window.sessionStorage.setItem(`board-copy-feedback:${result.data.board.id}`, summary);
    navigate(`/boards/${result.data.board.id}`);
  }, [boardId, copyingBoard]);

  if (!details && !notFound) {
    return (
      <section
        id="board-view"
        className="w-full px-4 pb-6 pt-5 sm:pb-8 sm:pt-7 flex flex-col gap-6 sm:gap-8 flex-1 min-h-0 overflow-hidden"
      />
    );
  }

  const board = details?.board;
  const finds = details?.finds ?? [];
  const isOwner = details?.isOwner ?? true;
  const addCtaBoardLabel = (editingLabel ? labelValue : board?.label)?.trim() || "this board";

  return (
    <section
      id="board-view"
      className="w-full px-4 pb-6 pt-5 sm:pb-8 sm:pt-7 flex flex-col gap-6 sm:gap-8 flex-1 min-h-0 overflow-hidden"
    >
      <div className="flex flex-col gap-4 sm:gap-5 shrink-0 min-w-0">
        <div className="flex flex-col gap-4 min-w-0 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          {isOwner && editingLabel ? (
            <input
              id="board-label"
              className="m-0 font-bold tracking-[-0.04em] leading-[0.94] text-[clamp(1.7rem,4vw,2.8rem)] bg-transparent border-none outline-none min-w-0 flex-1"
              value={labelValue}
              autoFocus
              onChange={(e) => setLabelValue(e.target.value)}
              onBlur={handleLabelSave}
              onKeyDown={handleLabelKeyDown}
            />
          ) : (
            <div
              className={`group flex items-center min-w-0 flex-1${isOwner ? " cursor-pointer" : ""}`}
              onClick={isOwner ? handleLabelClick : undefined}
            >
              <h1
                id="board-label"
                className="m-0 font-bold tracking-[-0.04em] leading-[1.08] text-[clamp(1.7rem,4vw,2.8rem)] truncate"
              >
                {board?.label || "Board"}
              </h1>
              {isOwner && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0"
                  style={{ width: "clamp(1rem, 2vw, 1.4rem)", height: "clamp(1rem, 2vw, 1.4rem)" }}
                >
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                </svg>
              )}
            </div>
          )}
          {isOwner && (
            <div className="flex items-center gap-3 self-start shrink-0 rounded-full border border-line/70 bg-background/60 px-3 py-2 sm:mt-1">
              <div className="flex items-center gap-2">
                <Switch
                  id="board-visibility"
                  checked={board?.visibility !== "private"}
                  onCheckedChange={handleVisibilityToggle}
                />
                <Label
                  htmlFor="board-visibility"
                  className="text-xs cursor-pointer"
                  title={board?.visibility === "private" ? "Board is private — click to make public" : "Board is public — click to make private"}
                >
                  {board?.visibility === "private" ? "Private" : "Public"}
                </Label>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDelete}
                className="text-danger hover:text-danger hover:bg-danger/10"
                title="Delete board"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </Button>
            </div>
          )}
        </div>
        {board?.ownerUsername && (
          <div className="pt-1 sm:pt-2">
            <OwnerAvatar username={board.ownerUsername} isOwner={isOwner} />
          </div>
        )}
      </div>

      <div
        id="board-finds"
        className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4 sm:gap-5 overflow-auto auto-rows-min flex-1 min-h-0 content-start"
      >
        {finds.length === 0 ? (
          <EmptyState text="No items on this board yet." />
        ) : (
          finds.map((find) => (
            <BoardProductCard key={find.id} product={find} onRemove={isOwner ? handleRemove : undefined} />
          ))
        )}
      </div>

      {isOwner && (
        <div className="w-full max-w-[760px] mx-auto pt-2 sm:pt-4 shrink-0 grid gap-4 sm:gap-5">
          <div className="grid justify-items-center gap-2 text-center">
            <h2
              className="m-0 max-w-full font-bold tracking-[-0.04em] text-[clamp(1.2rem,2.6vw,1.8rem)]"
              style={{ overflowWrap: "anywhere" }}
            >
              Add something to {addCtaBoardLabel}
            </h2>
          </div>
          <div>
            {feedback && <p className="text-sm text-foreground/50 mb-2">{feedback}</p>}
            <Composer
              ref={composerRef}
              textareaId="board-url-input"
              buttonId="board-url-submit"
              placeholder="Paste a URL to add an item..."
              buttonLabel="Add"
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      )}

      {!isOwner && (
        <div className="w-full max-w-[760px] mx-auto pt-2 sm:pt-4 shrink-0 grid gap-4 sm:gap-5">
          <div className="grid justify-items-center gap-2 text-center">
            <h2
              className="m-0 max-w-full font-bold tracking-[-0.04em] text-[clamp(1.2rem,2.6vw,1.8rem)]"
              style={{ overflowWrap: "anywhere" }}
            >
              Copy {addCtaBoardLabel} to your boards
            </h2>
            <p className="m-0 max-w-[44rem] text-sm text-foreground/60">
              Creates a new board in your collection with this board&apos;s title and copies its {finds.length} item{finds.length === 1 ? "" : "s"}. Items you already saved stay on their current boards instead of being moved.
            </p>
          </div>
          <div className="grid justify-items-center gap-2">
            {feedback && <p className="m-0 text-sm text-foreground/50">{feedback}</p>}
            <Button
              type="button"
              onClick={handleCopyBoard}
              disabled={copyingBoard}
              className="min-w-[220px]"
            >
              {copyingBoard ? "Copying..." : "Copy this board"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
